import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import MFASetup from './pages/MFASetup';
import MFAVerify from './pages/MFAVerify';
import Dashboard from './pages/Dashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SignDocument from './pages/SignDocument';
import SignReturn from './pages/SignReturn';
import SignHireDocument from './pages/SignHireDocument';

// Admin Pages
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import AccessControl from './pages/AccessControl';
import AuditLogs from './pages/AuditLogs';
import UserLifecycle from './pages/UserLifecycle';
import RoleManagement from './pages/RoleManagement';
import EquipmentAssignment from './pages/EquipmentAssignment';
import EquipmentLog from './pages/EquipmentLog';
import EquipmentReturn from './pages/EquipmentReturn';
import DocumentBrowser from './pages/DocumentBrowser';
import FacilityBoard from './pages/FacilityBoard';
import MasterDirectory from './pages/MasterDirectory';
import CountryManagement from './pages/CountryManagement';
import ClientManagement from './pages/ClientManagement';
import EmployeeDocuments from './pages/EmployeeDocuments';
import AssetManagement from './pages/AssetManagement';
import RecruitmentOnboarding from './pages/RecruitmentOnboarding';
import ChangePassword from './pages/ChangePassword';

import './App.css';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Navbar />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/mfa-verify" element={<MFAVerify />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/sign/return/:token" element={<SignReturn />} />
          <Route path="/sign/:token" element={<SignDocument />} />
          <Route path="/sign-hire/:token" element={<SignHireDocument />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/* Protected Routes */}
          <Route
            path="/mfa-setup"
            element={
              <PrivateRoute>
                <MFASetup />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_dashboard">
                <AdminDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_users">
                <UserManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/access-control"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_access">
                <AccessControl />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_audit">
                <AuditLogs />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/lifecycle"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_lifecycle">
                <UserLifecycle />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/roles"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_roles">
                <RoleManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/equipment-assignment"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_equip_assign">
                <EquipmentAssignment />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/equipment-log"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_equip_log">
                <EquipmentLog />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/equipment-return"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_equip_return">
                <EquipmentReturn />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/documents"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_documents">
                <DocumentBrowser />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/facility"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_facility">
                <FacilityBoard />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/master-directory"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_directory">
                <MasterDirectory />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/countries"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_countries">
                <CountryManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_clients">
                <ClientManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/employee-documents"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_emp_docs">
                <EmployeeDocuments />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/assets"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_assets">
                <AssetManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/recruitment/onboarding"
            element={
              <PrivateRoute adminRequired={true} requiredPage="admin_recruitment">
                <RecruitmentOnboarding />
              </PrivateRoute>
            }
          />

          {/* Redirect root to dashboard if authenticated, otherwise to login */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* 404 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
